import type { CampaignUserApi } from "../../../../api/campaigns";
import type {
  CampaignCharacter,
  CampaignUser,
  CharacterTier,
  UserActivityStatus,
} from "../types";

const splitName = (fullName?: string, email?: string) => {
  const fallback = email?.split("@")[0] ?? "Unknown";
  if (!fullName) {
    return {
      firstName: fallback,
      lastName: "",
    };
  }

  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return {
      firstName: fallback,
      lastName: "",
    };
  }

  const [firstName, ...rest] = parts;
  return {
    firstName: firstName ?? fallback,
    lastName: rest.join(" "),
  };
};

const resolveCharacterTier = (
  characterId: string,
  characterLibrary: CampaignCharacter[]
): CharacterTier => {
  const found = characterLibrary.find((character) => character.id === characterId);
  return found?.tier ?? "free";
};

const deriveStatus = (user: CampaignUserApi): UserActivityStatus => {
  if (!user.characters || user.characters.length === 0) {
    return "no-chat";
  }

  if (!user.lastActive) {
    return "inactive";
  }

  const lastActiveDate = new Date(user.lastActive);
  if (Number.isNaN(lastActiveDate.getTime())) {
    return "inactive";
  }

  const millisecondsPerDay = 1000 * 60 * 60 * 24;
  const daysSinceActive =
    (Date.now() - lastActiveDate.getTime()) / millisecondsPerDay;

  if (daysSinceActive <= 7) {
    return "active";
  }
  if (daysSinceActive <= 30) {
    return "inactive";
  }
  return "churned";
};

const formatBadge = (value?: string) => {
  if (!value) return undefined;
  return value
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const buildBadges = (user: CampaignUserApi): string[] => {
  const badges: string[] = [];
  if (!user.characters || user.characters.length === 0) {
    badges.push("No chats yet");
  }
  if ((user.totalTokens ?? 0) >= 50000) {
    badges.push("Token Whale");
  }
  const membership = formatBadge(user.membershipBadge);
  if (membership) {
    badges.push(membership);
  }
  const tokenStatus = formatBadge(user.tokenBadge);
  if (tokenStatus) {
    badges.push(tokenStatus);
  }
  return badges;
};

export const mapCampaignUsersFromApi = (
  apiUsers: CampaignUserApi[] | undefined,
  characterLibrary: CampaignCharacter[]
): CampaignUser[] => {
  if (!Array.isArray(apiUsers)) {
    return [];
  }

  return apiUsers.map((user) => {
    const nameParts = splitName(user.name, user.email);
    const characters = (user.characters ?? []).map((character) => ({
      characterId: character.characterId,
      name: character.characterName,
      tokensUsed: character.tokens ?? 0,
      tier: resolveCharacterTier(character.characterId, characterLibrary),
    }));

    const campaignMemberships =
      user.campaigns?.map((campaign) => ({
        campaignId: campaign.campaignId,
        campaignName: campaign.campaignName,
        lastSentAt: campaign.lastSentAt,
      })) ?? [];

    const primaryCampaign = campaignMemberships[0];

    const reportedTokens = user.totalTokens ?? 0;
    const derivedTotalTokens = characters.reduce(
      (total, character) => total + character.tokensUsed,
      0
    );

    return {
      id: user.userId,
      email: user.email,
      firstName: nameParts.firstName,
      lastName: nameParts.lastName,
      lastInteractionAt: user.lastActive ?? "",
      timezone: "",
      characters,
      totalTokens: reportedTokens || derivedTotalTokens,
      totalConversations: user.totalConversations,
      status: deriveStatus(user),
      badges: buildBadges(user),
      currentSequenceStep: undefined,
      lastEmailSentAt: undefined,
      isPaidSubscriber: user.isActivelyPaying,
      membershipBadge: user.membershipBadge,
      tokenBadge: user.tokenBadge,
      ranOutOfTokens: user.ranOutOfTokens,
      everRanOutOfTokens: user.everRanOutOfTokens,
      campaignId: primaryCampaign?.campaignId,
      campaignName: primaryCampaign?.campaignName,
      campaigns: campaignMemberships,
    };
  });
};
